/**
 * Account settings — personal information, language and password.
 *
 * Extracted from Dashboard.tsx, which owned this modal and could therefore
 * only ever show it to tenants. Owner-portal accounts are redirected out of
 * /dashboard on mount, so the account screen was unreachable for them and the
 * Settings page's "Name, contact details, language and password" link was a
 * dead end.
 *
 * The tenant Documents tab (identity / salary uploads) deliberately did NOT
 * come along. It is tenant-only — proof of income means nothing for a
 * landlord — and it is where nearly all of the coupling to Dashboard's state
 * lived. Dashboard passes it back in through `documentsSlot`, so that markup
 * still runs in the scope that owns its upload state, and the owner portal
 * simply never provides one.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertCircle,
  Edit2,
  FileCheck,
  Globe,
  Loader2,
  Lock,
  Save,
  Settings,
  User,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { LanguagePreferenceCard } from "@/components/settings/LanguagePreferenceCard";
import { PasswordResetCard } from "@/components/settings/PasswordResetCard";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { isUnderMinimumAge, latestAdultBirthDate } from "@/lib/adult-age";
import { supabase } from "@/lib/supabase";
import type { ReactNode } from "react";

export type AccountSettingsTab =
  | "profile"
  | "documents"
  | "language"
  | "password";

export function AccountSettingsDialog({
  open,
  onOpenChange,
  initialTab = "profile",
  documentsSlot,
  documentsHasIssue = false,
  phoneFallback = "",
  onClosed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTab?: AccountSettingsTab;
  /** Tenant-only Documents panel, rendered by Dashboard in its own scope. */
  documentsSlot?: ReactNode;
  documentsHasIssue?: boolean;
  /**
   * Phone to offer when the profile has none. Dashboard passes the number off
   * the tenant's bookings; the owner portal has no bookings and passes none.
   */
  phoneFallback?: string;
  /** Extra cleanup the host needs when the dialog closes. */
  onClosed?: () => void;
}) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const queryClient = useQueryClient();

  const [settingsTab, setSettingsTab] =
    useState<AccountSettingsTab>(initialTab);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: "",
    email: "",
    phone: "",
    dateOfBirth: "",
  });

  // Reopening on a different tab (the ?settings= deep link, or the owner menu)
  // must actually land there rather than reuse the last tab.
  useEffect(() => {
    if (open) setSettingsTab(initialTab);
  }, [open, initialTab]);

  /**
   * Own the profile read rather than take it as a prop: the owner portal has
   * no Dashboard around it to supply one, and both hosts want the same row.
   */
  const { data: profile } = useQuery({
    queryKey: ["user-profile", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user!.id)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const accountEmail = (
    user?.email ||
    (profile as { email?: string } | null)?.email ||
    ""
  ).trim();
  const needsProfileCompletion = (() => {
    if (!profile) return false;
    const dob = (profile as { date_of_birth?: string | null }).date_of_birth;
    return !dob || !String(dob).trim();
  })();

  // Seed the form from the profile whenever the dialog opens.
  useEffect(() => {
    if (!open || !profile) return;
    const pr = profile as Record<string, unknown>;
    setProfileForm({
      name: String(pr.name ?? ""),
      email: String(pr.email ?? user?.email ?? ""),
      phone: String(pr.phone ?? ""),
      dateOfBirth: String(pr.date_of_birth ?? ""),
    });
  }, [open, profile, user?.email]);

  const updateProfileMutation = useMutation({
    mutationFn: async ({
      name,
      email,
      phone,
      dateOfBirth,
    }: {
      name: string;
      email: string;
      phone: string;
      dateOfBirth?: string;
    }) => {
      if (!user?.id) throw new Error("User not authenticated");

      // Fetch current values for audit trail.
      const { data: prevProfileRaw, error: prevError } = await supabase
        .from("profiles")
        .select("name, email, phone, date_of_birth")
        .eq("id", user.id)
        .single();
      if (prevError) throw prevError;
      const prevProfile = prevProfileRaw;

      const phoneTrim = phone.trim() || null;
      const dobTrim = (dateOfBirth ?? "").trim() || null;
      // COL-629: the profiles trigger refuses this too; refusing here keeps
      // the message readable (onError toasts it). Only a CHANGED date is
      // judged, as the trigger does, so a date already on file never blocks
      // a name or phone edit.
      if (
        dobTrim !== (prevProfile?.date_of_birth ?? null) &&
        isUnderMinimumAge(dobTrim)
      ) {
        throw new Error(
          language === "de"
            ? "Du musst mindestens 18 Jahre alt sein."
            : "You must be at least 18 years old."
        );
      }

      // Still one statement. The name/phone change is carried to this owner's
      // `lessors` record by a trigger, inside this same transaction, so this
      // screen — which the owner portal shares, see where Dashboard mounts it —
      // no longer leaves the Owners area showing the old details. See
      // 20260816140000_account_profile_syncs_to_owner_record.sql.
      const { data: updatedProfile, error: updateError } = await supabase
        .from("profiles")
        .update({
          name,
          email,
          phone: phoneTrim,
          date_of_birth: dobTrim,
        } as never)
        .eq("id", user.id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Best-effort audit log insert (timeline uses this)
      const auditAt = new Date().toISOString();
      const { error: auditError } = await supabase.from("audit_logs").insert({
        action: "profile_updated",
        resource_type: "profile",
        resource_id: user.id,
        user_id: user.id,
        created_at: auditAt,
        old_values: {
          name: prevProfile?.name ?? null,
          email: prevProfile?.email ?? null,
          phone: (prevProfile as { phone?: string | null })?.phone ?? null,
          date_of_birth:
            (prevProfile as { date_of_birth?: string | null })?.date_of_birth ??
            null,
        },
        new_values: {
          name: updatedProfile?.name ?? null,
          email: updatedProfile?.email ?? null,
          phone: (updatedProfile as { phone?: string | null })?.phone ?? null,
          date_of_birth:
            (updatedProfile as { date_of_birth?: string | null })
              ?.date_of_birth ?? null,
        },
        metadata: { source: "dashboard_settings" },
      });
      if (auditError) {
        // Don't block profile update if audit log fails (RLS/triggers/etc.)
        console.warn("Failed to write profile audit log", auditError);
      }

      return updatedProfile;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-profile", user?.id] });
      toast.success("Profile updated successfully!");
      setIsEditingProfile(false);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update profile");
    },
  });

  const settingsLabel = language === "de" ? "Einstellungen" : "Settings";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          setIsEditingProfile(false);
          setSettingsTab("profile");
          onClosed?.();
        }
      }}
    >
      {/* dvh, not vh: on a phone the browser chrome eats part of `vh`, so a
          90vh sheet centred on the viewport pushed its own title off the top and
          the Close button off the bottom. dvh tracks what is actually visible.
          Mobile also gets the full height — a settings screen on a phone has no
          use for a peek of the page behind it. */}
      <DialogContent className="sm:max-w-[920px] h-[100dvh] max-h-[100dvh] rounded-none sm:h-[90dvh] sm:max-h-[90dvh] sm:rounded-lg p-0 overflow-hidden border-border/60 shadow-2xl flex flex-col">
        <div className="p-5 sm:p-6 border-b border-border/60 bg-gradient-to-br from-background via-background to-muted/30">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
              <Settings className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">
                {settingsLabel}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-1">
                {language === "de"
                  ? "Persönliche Daten, Sprache und Passwort verwalten"
                  : "Manage personal information, language, and password"}
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0">
          <Tabs
            value={settingsTab}
            onValueChange={(v) => setSettingsTab(v as AccountSettingsTab)}
            className="w-full h-full"
          >
            <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] h-full min-h-0">
              {/* Mobile: one scrollable row, so a label like "Personal
                  information" reads in full instead of being truncated into a
                  half-width grid cell with a stray third item below it. */}
              <div className="border-b md:border-b-0 md:border-r border-border/60 bg-muted/20 p-3 md:p-4 overflow-x-auto md:overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <TabsList className="flex w-max md:w-full md:grid md:grid-cols-1 h-auto gap-2 bg-transparent p-0">
                  <TabsTrigger
                    value="profile"
                    className="justify-start gap-2 rounded-xl data-[state=active]:bg-background data-[state=active]:shadow-sm"
                  >
                    <User className="h-4 w-4" />
                    <span className="truncate">
                      {language === "de"
                        ? "Persönliche Daten"
                        : "Personal information"}
                    </span>
                    {needsProfileCompletion && (
                      <span className="ml-auto w-2 h-2 bg-destructive rounded-full shrink-0" />
                    )}
                  </TabsTrigger>
                  {documentsSlot ? (
                    <TabsTrigger
                      value="documents"
                      className="justify-start gap-2 rounded-xl data-[state=active]:bg-background data-[state=active]:shadow-sm"
                    >
                      <FileCheck className="h-4 w-4" />
                      {language === "de" ? "Dokumente" : "Documents"}
                      {documentsHasIssue && (
                        <span className="ml-auto w-2 h-2 bg-destructive rounded-full shrink-0" />
                      )}
                    </TabsTrigger>
                  ) : null}
                  <TabsTrigger
                    value="language"
                    className="justify-start gap-2 rounded-xl data-[state=active]:bg-background data-[state=active]:shadow-sm"
                  >
                    <Globe className="h-4 w-4" />
                    {language === "de" ? "Sprache" : "Language"}
                  </TabsTrigger>
                  <TabsTrigger
                    value="password"
                    className="justify-start gap-2 rounded-xl data-[state=active]:bg-background data-[state=active]:shadow-sm"
                  >
                    <Lock className="h-4 w-4" />
                    <span className="truncate">
                      {language === "de"
                        ? "Passwort zurücksetzen"
                        : "Reset password"}
                    </span>
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="min-h-0 flex flex-col">
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                  <div className="p-4 sm:p-6 space-y-5 pb-40">
                    <TabsContent value="profile" className="m-0 space-y-5">
                      {needsProfileCompletion && (
                        <div className="flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/10 p-4">
                          <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-destructive">
                              {language === "de"
                                ? "Aktion erforderlich"
                                : "Action needed"}
                            </p>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              {language === "de"
                                ? "Bitte ergänze dein Geburtsdatum, um dein Profil zu vervollständigen. Tippe auf „Bearbeiten“, gib das Datum ein und speichere."
                                : "Please add your date of birth to complete your profile. Tap “Edit”, enter the date, and save."}
                            </p>
                          </div>
                        </div>
                      )}
                      <Card className="rounded-2xl border border-border/60 shadow-sm">
                        <div className="p-5 sm:p-6">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="text-base sm:text-lg font-semibold text-foreground">
                                {language === "de"
                                  ? "Persönliche Daten"
                                  : "Personal information"}
                              </h3>
                              <p className="text-sm text-muted-foreground mt-0.5">
                                {language === "de"
                                  ? "Persönliche Daten verwalten"
                                  : "Manage your personal details"}
                              </p>
                            </div>
                            {!isEditingProfile && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setIsEditingProfile(true)}
                                className="rounded-xl border-border/60"
                              >
                                <Edit2 className="w-4 h-4 mr-2" />
                                {language === "de" ? "Bearbeiten" : "Edit"}
                              </Button>
                            )}
                          </div>

                          {isEditingProfile ? (
                            <div className="grid gap-4 mt-5">
                              <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                  <Label
                                    htmlFor="profile-name"
                                    className="text-sm font-medium"
                                  >
                                    {language === "de"
                                      ? "Vollständiger Name"
                                      : "Full name"}
                                  </Label>
                                  <Input
                                    id="profile-name"
                                    value={profileForm.name}
                                    onChange={(e) =>
                                      setProfileForm({
                                        ...profileForm,
                                        name: e.target.value,
                                      })
                                    }
                                    placeholder={
                                      language === "de"
                                        ? "Vollständigen Namen eingeben"
                                        : "Enter your full name"
                                    }
                                    className="h-11 rounded-xl"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label
                                    htmlFor="profile-phone"
                                    className="text-sm font-medium"
                                  >
                                    {language === "de"
                                      ? "Telefonnummer"
                                      : "Phone number"}
                                  </Label>
                                  <Input
                                    id="profile-phone"
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel"
                                    value={profileForm.phone}
                                    onChange={(e) =>
                                      setProfileForm({
                                        ...profileForm,
                                        phone: e.target.value,
                                      })
                                    }
                                    placeholder={
                                      language === "de"
                                        ? "z. B. +49 123 4567890"
                                        : "e.g. +49 123 4567890"
                                    }
                                    className="h-11 rounded-xl"
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label
                                  htmlFor="profile-email"
                                  className="text-sm font-medium"
                                >
                                  {language === "de"
                                    ? "E‑Mail-Adresse"
                                    : "Email address"}
                                </Label>
                                <Input
                                  id="profile-email"
                                  type="email"
                                  value={profileForm.email}
                                  onChange={(e) =>
                                    setProfileForm({
                                      ...profileForm,
                                      email: e.target.value,
                                    })
                                  }
                                  placeholder={
                                    language === "de"
                                      ? "E‑Mail-Adresse eingeben"
                                      : "Enter your email address"
                                  }
                                  className="h-11 rounded-xl"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label
                                  htmlFor="profile-dob"
                                  className="text-sm font-medium"
                                >
                                  {language === "de"
                                    ? "Geburtsdatum"
                                    : "Date of birth"}
                                </Label>
                                <Input
                                  id="profile-dob"
                                  type="date"
                                  value={profileForm.dateOfBirth}
                                  max={latestAdultBirthDate()}
                                  onChange={(e) =>
                                    setProfileForm({
                                      ...profileForm,
                                      dateOfBirth: e.target.value,
                                    })
                                  }
                                  className="h-11 rounded-xl"
                                />
                                <p
                                  className={`text-xs ${
                                    needsProfileCompletion
                                      ? "text-destructive font-medium"
                                      : "text-muted-foreground"
                                  }`}
                                >
                                  {needsProfileCompletion
                                    ? language === "de"
                                      ? "Bitte ergänze dein Geburtsdatum, um dein Profil zu vervollständigen."
                                      : "Please add your date of birth to complete your profile."
                                    : language === "de"
                                      ? "Wird für deinen Mietvertrag verwendet."
                                      : "Used for your rental contract."}
                                </p>
                              </div>

                              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
                                <Button
                                  variant="outline"
                                  className="rounded-xl"
                                  onClick={() => {
                                    setIsEditingProfile(false);
                                    if (profile) {
                                      const profPhone = (
                                        profile as { phone?: string | null }
                                      ).phone?.trim();
                                      const fromBooking = phoneFallback ?? "";
                                      setProfileForm({
                                        name: profile.name || "",
                                        email:
                                          profile.email || user?.email || "",
                                        phone: profPhone || fromBooking || "",
                                        dateOfBirth:
                                          (
                                            profile as {
                                              date_of_birth?: string | null;
                                            }
                                          ).date_of_birth ?? "",
                                      });
                                    }
                                  }}
                                  disabled={updateProfileMutation.isPending}
                                >
                                  {language === "de" ? "Abbrechen" : "Cancel"}
                                </Button>
                                <Button
                                  className="rounded-xl"
                                  onClick={() => {
                                    updateProfileMutation.mutate({
                                      name: profileForm.name,
                                      email: profileForm.email,
                                      phone: profileForm.phone,
                                      dateOfBirth: profileForm.dateOfBirth,
                                    });
                                  }}
                                  disabled={
                                    updateProfileMutation.isPending ||
                                    !profileForm.name.trim() ||
                                    !profileForm.email.trim()
                                  }
                                >
                                  {updateProfileMutation.isPending ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      {language === "de"
                                        ? "Speichern…"
                                        : "Saving…"}
                                    </>
                                  ) : (
                                    <>
                                      <Save className="w-4 h-4 mr-2" />
                                      {language === "de" ? "Speichern" : "Save"}
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="grid gap-3 mt-5">
                              <div className="grid gap-3 sm:grid-cols-3">
                                <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    {language === "de" ? "Name" : "Name"}
                                  </p>
                                  <p className="mt-1 text-sm font-semibold text-foreground truncate">
                                    {profile?.name ||
                                      user?.email?.split("@")[0] ||
                                      (language === "de"
                                        ? "Nicht hinterlegt"
                                        : "Not set")}
                                  </p>
                                </div>
                                <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    {language === "de" ? "E‑Mail" : "Email"}
                                  </p>
                                  <p className="mt-1 text-sm font-semibold text-foreground truncate">
                                    {profile?.email ||
                                      user?.email ||
                                      (language === "de"
                                        ? "Nicht hinterlegt"
                                        : "Not set")}
                                  </p>
                                </div>
                                <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    {language === "de" ? "Telefon" : "Phone"}
                                  </p>
                                  <p className="mt-1 text-sm font-semibold text-foreground tabular-nums truncate">
                                    {(
                                      profile as { phone?: string | null }
                                    )?.phone?.trim() ||
                                      phoneFallback ||
                                      (language === "de"
                                        ? "Nicht hinterlegt"
                                        : "Not set")}
                                  </p>
                                </div>
                                <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    {language === "de"
                                      ? "Geburtsdatum"
                                      : "Date of birth"}
                                  </p>
                                  <p className="mt-1 text-sm font-semibold text-foreground tabular-nums truncate">
                                    {(() => {
                                      const dob = (
                                        profile as {
                                          date_of_birth?: string | null;
                                        }
                                      )?.date_of_birth;
                                      const m = dob?.match(
                                        /^(\d{4})-(\d{2})-(\d{2})/
                                      );
                                      if (m) {
                                        return language === "de"
                                          ? `${m[3]}.${m[2]}.${m[1]}`
                                          : `${m[3]}/${m[2]}/${m[1]}`;
                                      }
                                      return language === "de"
                                        ? "Nicht hinterlegt"
                                        : "Not set";
                                    })()}
                                  </p>
                                </div>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {language === "de"
                                  ? "Hinweis: Telefonnummer kann aus Ihrer Zimmerbewerbung übernommen werden."
                                  : "Note: phone number can be synced from your room application."}
                              </p>
                            </div>
                          )}
                        </div>
                      </Card>
                    </TabsContent>

                    <TabsContent value="language" className="m-0 space-y-5">
                      <LanguagePreferenceCard />
                    </TabsContent>

                    {documentsSlot}

                    <TabsContent value="password" className="m-0 space-y-5">
                      <PasswordResetCard email={accountEmail} />
                    </TabsContent>
                  </div>
                </div>
              </div>
            </div>
          </Tabs>
        </div>

        <DialogFooter className="shrink-0 px-5 sm:px-6 py-4 border-t border-border/60 bg-background pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="w-full flex items-center justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-xl border-border/60"
            >
              {language === "de" ? "Schließen" : "Close"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
